import { motion } from "framer-motion";
import { Ban, Check, Salad, ClipboardList, MessageSquare, CalendarCheck, Flame } from "lucide-react";
import SectionHeading from "./SectionHeading";

const NOT_THIS = [
  "Commercial meal replacement shakes sold for commission",
  "Synthetic MLM powders or blind fat burners",
  "Supplements pushed without a single blood report",
  "Generic PDF plans copy-pasted for every client",
  "Starvation calories you cannot sustain past week two",
];

const THIS_IS = [
  "Real Indian home food — bhakri, sprouts, paneer, curd, seasonal veg",
  "Low glycaemic index foods with zero added sugar",
  "Protein prescribed in grams at every single meal",
  "Ghee, butter and cold-pressed oils instead of refined oils",
  "A protocol rebuilt around your body, reports and work hours",
  "Targeted supplementation only when blood work actually demands it",
];

const STEPS = [
  {
    icon: ClipboardList,
    n: "01",
    t: "Assessment",
    d: "Your weight history, medical flags, thyroid/PCOS/sugar status, work shift, kitchen and food preferences — before a single meal is written.",
  },
  {
    icon: Salad,
    n: "02",
    t: "Food-First Protocol",
    d: "Low-GI, zero-sugar plan with exact gram portions, timed around your body clock, with dinner early at 6:30–7:30 PM. Whole food carries the plan — nothing is outsourced to a powder.",
  },
  {
    icon: Flame,
    n: "03",
    t: "Real-Food Swaps",
    d: "Bhakri instead of chapati, rock salt instead of table salt, berries instead of banana — swaps you can cook tonight with what your family already eats.",
  },
  {
    icon: MessageSquare,
    n: "04",
    t: "Daily WhatsApp Check-In",
    d: "Photograph every meal and send it across. No waiting for approval — corrections come back the same day so you never drift for a week.",
  },
  {
    icon: CalendarCheck,
    n: "05",
    t: "15-Day Checkpoint",
    d: "Morning weigh-ins on the same scale, then a full review every fortnight. Portions, protein and meal count are re-tuned as your body responds.",
  },
];

export default function Approach() {
  return (
    <section id="approach" className="relative py-24 lg:py-32">
      <div
        aria-hidden="true"
        className="absolute left-0 top-1/4 h-80 w-80 rounded-full bg-accent/8 blur-[130px]"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <SectionHeading
          eyebrow="The Method"
          title="Whole-Food First. Clinically Honest."
          sub="Whole-food first approach. Targeted, clinical supplementation is recommended only when deep medical history or blood test markers demand it — no blind fat burners or useless powders. Every result on this page was built on ordinary Indian home food, structured correctly."
        />

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl border border-rose-500/25 bg-panel/80 p-8 lg:p-10"
            data-testid="approach-not-this-card"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-rose-400/90">What this is not</p>
            <h3 className="mt-4 font-display text-2xl font-extrabold uppercase text-white">
              No products. No pyramid.
            </h3>
            <ul className="mt-7 space-y-4">
              {NOT_THIS.map((item) => (
                <li key={item} className="flex gap-3 text-sm text-slate-400 leading-relaxed">
                  <Ban className="h-4 w-4 shrink-0 mt-0.5 text-rose-400/80" />
                  <span className="line-through decoration-rose-400/40">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="rounded-3xl border border-accent/35 bg-gradient-to-br from-panel to-ink p-8 lg:p-10 shadow-glow-emerald"
            data-testid="approach-this-is-card"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-emerald-400">What you actually get</p>
            <h3 className="mt-4 font-display text-2xl font-extrabold uppercase text-white">
              Only food, measured properly.
            </h3>
            <ul className="mt-7 space-y-4">
              {THIS_IS.map((item) => (
                <li key={item} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
                  <Check className="h-4 w-4 shrink-0 mt-0.5 text-emerald-400" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <div className="mt-6 grid lg:grid-cols-12 gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`${
                i < 2 ? "lg:col-span-6" : "lg:col-span-4"
              } group rounded-3xl border border-line bg-panel p-8 transition-all duration-300 hover:-translate-y-1 hover:border-accent/40`}
              data-testid={`approach-step-${i}`}
            >
              <div className="flex items-start justify-between">
                <s.icon className="h-7 w-7 text-emerald-400 transition-transform duration-300 group-hover:scale-110" />
                <span className="font-mono text-xs tracking-[0.25em] text-gold">{s.n}</span>
              </div>
              <h4 className="mt-6 font-display text-xl font-bold uppercase tracking-wide text-white">{s.t}</h4>
              <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
